import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { DropdownChangeEvent } from 'primeng/dropdown';
import { City } from 'src/app/modules/entities/city';
import { Locality } from 'src/app/modules/entities/locality';
import { Person } from 'src/app/modules/entities/person';
import { CategoryService } from 'src/app/modules/entities/service';
import { ServicePlace } from 'src/app/modules/entities/service-place';
import { CityService } from 'src/app/modules/services/city.service';
import { LocalityService } from 'src/app/modules/services/locality.service';
import { PersonService } from 'src/app/modules/services/person.service';
import { ServicePlaceService } from 'src/app/modules/services/service-place.service';
import { ServiceCategoryService } from 'src/app/modules/services/service.service';
import { errorDescription, url } from 'src/app/modules/util/constants';

interface Gender {
  name: string;
  value: number;
}

@Component({
  selector: 'app-edit-profile',
  templateUrl: './edit-profile.component.html',
  styleUrls: ['./edit-profile.component.css']
})
export class EditProfileComponent implements OnInit {

  constructor(private router: Router, private route: ActivatedRoute, private fb: FormBuilder,
    private cityService: CityService, private localityService: LocalityService, private personService: PersonService, private messageService: MessageService,
    private servicePlaceService: ServicePlaceService, private serviceCategoryService: ServiceCategoryService) {
    this.personForm = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(100), Validators.pattern('[a-zA-ZáéíóúÁÉÍÓÚÜüÑñ\\s]+')]],
      age: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(2), Validators.pattern('[0-9]+'), Validators.min(18)]],
      locality: ['', [Validators.required]],
      city: ['', [Validators.required]],
      servicePlaces: ['', [Validators.required]],
      pricePerHour: [null, [Validators.required, Validators.minLength(5), Validators.maxLength(6), Validators.pattern('[0-9]+'), Validators.min(10000), 
        Validators.max(999999)]],
      schedule: ['', [Validators.required, Validators.maxLength(100)]],
      description: ['', [Validators.required, Validators.minLength(200), Validators.maxLength(2000)]],
      gender: ['', [Validators.required]],
      idDocument: [''],
      notes: [''],
      showPrice: [true]
    });
    this.urlId = this.route.snapshot.paramMap.get('id');
  }

  urlId: string | null;
  person: Person | undefined = undefined;
  personForm: FormGroup;
  cities: City[] | undefined;
  localities: Locality[] | undefined;
  servicePlaces: ServicePlace[] = [];
  services: CategoryService[] = [];
  serviceOptionsByCategory: { [key: string]: CategoryService[] } = {};
  categories: string[] = [];
  genders: Gender[] | undefined;
  isAdmin: boolean = false;

  ngOnInit() {
    if (this.urlId) {
      this.isAdmin = sessionStorage.getItem('loggedUser') === 'admin';
      if (!this.isAdmin && sessionStorage.getItem('loggedUser') !== this.urlId) {
        this.urlId = sessionStorage.getItem('loggedUser')!;
        this.router.navigate([url['editar-perfil'], sessionStorage.getItem('loggedUser')]);
      }
      this.genders = [
        {
          name: 'Mujer',
          value: 1
        }
      ]
      this.personService.getFullProfile(this.urlId, 0).subscribe(response => {
        this.person = response.data as Person;
        this.personForm.get('name')?.setValue(this.person.name);
        this.personForm.get('age')?.setValue(this.person.age);
        this.personForm.get('servicePlaces')?.setValue(this.person.servicePlaces);
        this.personForm.get('pricePerHour')?.setValue(Math.abs(this.person.pricePerHour));
        this.personForm.get('showPrice')?.setValue(this.person.pricePerHour > 0);
        this.personForm.get('schedule')?.setValue(this.person.schedule);
        this.personForm.get('description')?.setValue(this.person.description);
        this.personForm.get('gender')?.setValue(this.genders?.find(x => x.name === this.person?.gender));
        if (this.isAdmin) {
          this.personForm.get('idDocument')?.setValue(this.person.idDocument ? this.person.idDocument : '');
          this.personForm.get('notes')?.setValue(this.person.notes ? this.person.notes : '');
        }
        this.getVisibleCities();
        this.getServicePlacesAsOptions();
        this.getServices();
        this.getVisibleLocalities(this.person.city.idcity!);
      });
    }
  }

  getVisibleCities() {
    this.cityService.getCities().subscribe(response => {
      if (response.status === 'success') {
        this.cities = response.data.filter(city => city.visible === 1);
        this.personForm.get('city')?.setValue(this.cities?.find(x => x.id === this.person?.city.idcity));
      }
    });
  }

  getVisibleLocalities(cityId: number) {
    this.localityService.getVisibleLocalitiesByCity(cityId).subscribe(response => {
      if (response.status === 'success') {
        this.localities = response.data;
        this.personForm.get('locality')?.setValue(this.localities?.find(x => x.id === this.person?.locality.idlocality));
      }
    });
  }

  getServicePlacesAsOptions() {
    this.servicePlaceService.getServicePlaces().subscribe(response => {
      if (response.status === 'success') {
        this.servicePlaces = response.data;
        this.personForm.get('locality')?.setValue(this.localities?.find(x => x.id === this.person?.locality.idlocality));
      }
    });
  }

  getServices() {
    this.serviceCategoryService.getServices().subscribe(response => {
      if (response.status === 'success') {
        let services = response.data;
        for (let service of services) {
          if (this.serviceOptionsByCategory[service.category] === undefined) {
            this.serviceOptionsByCategory[service.category] = []
            this.categories.push(service.category);            
          }
          this.serviceOptionsByCategory[service.category].push(service);
        }
        for (let category of this.categories) {
          this.personForm.addControl('service-' + this.cleanUrlId(category), this.fb.control('', Validators.required));
          this.personForm.get('service-' + this.cleanUrlId(category))?.setValue(
            services.find(service => service.id === this.person?.services!.find(x => x.category === category)?.id)
          );
        }
      }
    });
  }

  onCitySelection(event: DropdownChangeEvent) {
    if (event.value.id) {
      this.localityService.getVisibleLocalitiesByCity(this.personForm.get('city')?.value.id).subscribe(response => {
        if (response.status === 'success') {
          this.localities = response.data;
          this.personForm.get('locality')?.enable();
        }
      });
    }
  }

  cleanName(name: string): string {
    name = name.replace(/\s+/g, ' ');
    name = name.replace(/[^A-Za-zÑñáéíóúÁÉÍÓÚÜü\s]/g, '');
    name = name.trim();
    name = name.replace(/(^\w|\s\w)(\S*)/g, (_, m1, m2) => m1.toUpperCase() + m2.toLowerCase());
    return name;
  }

  cleanUrlId(name: string): string {
    name = name.replace(/\s+/g, '-');
    name = name.replace(/-+/g, '-');
    name = name.replaceAll('Á', 'A');
    name = name.replaceAll('á', 'a');
    name = name.replaceAll('É', 'E');
    name = name.replaceAll('é', 'e');
    name = name.replaceAll('Í', 'I');
    name = name.replaceAll('í', 'i');
    name = name.replaceAll('Ó', 'o');
    name = name.replaceAll('ó', 'o');
    name = name.replaceAll('Ú', 'u');
    name = name.replaceAll('ú', 'u');
    name = name.replaceAll('Ü', 'u');
    name = name.replaceAll('ü', 'u');
    name = name.replaceAll('Ñ', 'N');
    name = name.replaceAll('ñ', 'n');
    name = name.replace(/[^A-Za-z-]/g, '');
    return name.toLowerCase();
  }

  updatePerson() {
    let pricePerHour = this.personForm.get('pricePerHour')?.value;
    if (this.personForm.get('showPrice')?.value)
      pricePerHour = Math.abs(pricePerHour);
    else
      pricePerHour = Math.abs(pricePerHour) * -1;
    let modifiedProperties: string[] = [];
    if (this.personForm.get('name')?.value !== this.person?.name)
        modifiedProperties.push('name');
    if (this.personForm.get('age')?.value !== this.person?.age)
      modifiedProperties.push('age');
    if (this.personForm.get('pricePerHour')?.value !== this.person?.pricePerHour || this.person?.pricePerHour !== pricePerHour)
      modifiedProperties.push('pricePerHour');
    if (this.personForm.get('schedule')?.value !== this.person?.schedule)
      modifiedProperties.push('schedule');
    if (this.personForm.get('description')?.value !== this.person?.description)
      modifiedProperties.push('description');
    if (this.isAdmin) {
      if (this.personForm.get('idDocument')?.value !== this.person?.idDocument)
        modifiedProperties.push('idDocument');
      if (this.personForm.get('notes')?.value !== this.person?.notes)
        modifiedProperties.push('notes');
    }
    if (this.personForm.get('gender')?.value.name !== this.person?.gender)
      modifiedProperties.push('gender');
    if (this.personForm.get('city')?.value.id !== this.person?.city.idcity)
      modifiedProperties.push('city');
    if (this.personForm.get('locality')?.value.id !== this.person?.locality.idlocality)
      modifiedProperties.push('locality');
    if (this.personForm.get('servicePlaces')?.value.length !== this.person?.servicePlaces?.length) {
      modifiedProperties.push('servicePlaces');
    } else {
      let found: ServicePlace | undefined;
      for (let servicePlace of this.personForm.get('servicePlaces')?.value) {
        found = this.person?.servicePlaces?.find(x => x.id === servicePlace.id);
        if (found === undefined)
          modifiedProperties.push('servicePlaces');
      }
    }
    let matchingServices: CategoryService[] = [];
    let modifiedServices: CategoryService[] = [];
    for (let category of this.categories) {
      let categoryService = this.person?.services?.find( x => x.id === this.personForm.get('service-' + this.cleanUrlId(category))?.value.id);
      if (categoryService !== undefined) {
        matchingServices.push(categoryService);
      } else {
        modifiedServices.push(this.personForm.get('service-' + this.cleanUrlId(category))?.value);
      }
    }
    if (matchingServices.length !== this.person?.services?.length)
      modifiedProperties.push('services');
    if (modifiedProperties.length > 0) {
      let modifiedValues: {[key: string]: string | number | City | Locality | ServicePlace | CategoryService[]} = {};
      for (let property of modifiedProperties) {
        modifiedValues[property] = this.personForm.get(property)?.value;
        if (property === 'pricePerHour')
          modifiedValues[property] = pricePerHour;
        if (property === 'services')
          modifiedValues[property] = modifiedServices;
      }
      this.personService.updatePerson(this.urlId!, modifiedValues).subscribe({
        next: response => {
          if (response.status === 'success') {
            this.router.navigate([url['perfil'] + '/' + response.data.urlId]);
            this.messageService.add({ severity: 'success', detail: 'Información modificada correctamente.' });
          } else {
            this.router.navigate([url['perfil'] + '/' + response.data.urlId]);
            this.messageService.add({ severity: 'error', detail: 'Ocurrió un error. Intente nuevamente.' });
          }
        },
        error: error => {
          this.messageService.add({severity: 'error', detail: errorDescription[error.error.error]});
          this.router.navigate([url['login']]);
        }
      });
    } else {
      this.router.navigate([url['perfil'] + '/' + this.urlId]);
    }
  }

  goToProfile() {
    this.router.navigate([url['perfil'] + '/' + this.urlId]);
  }
}
